import type { Campaign, AutomationRules } from "@type/agent";
import {
    createAgentCampaign,
    updateAgentCampaign,
    updateCampaignFlow,
    updateCampaignEvaluation,
    updateCampaignAutomation,
} from "@service/agent";
import { withRetry } from "@util/retry";
import CampaignFormContainer from "./campaign-form";

const emptyCampaign: Campaign = {
    id: "",
    name: "",
    agentName: "",
    useCase: "lead_qualification",
    status: "draft",
    language: "en",
    timezone: "Asia/Kuala_Lumpur",
    maxDurationSeconds: 600,
    openingScript: "",
    closingScript: "",
    coreQuestions: [],
    conditionalLogic: {},
    conversationMode: "scripted",
    objective: "",
    knowledgeDocuments: [],
    conversationGoal: "",
    topicAreas: [],
    probingDepth: "medium",
    guardrails: "",
    triggerMethod: "manual",
    retryAttempts: 0,
    retryDelayMinutes: 5,
    scheduleConfig: { timingMode: "immediate" },
    evaluationCriteria: [],
    contactCount: 0,
    sessionCount: 0,
    recentActivity: [],
    createdAt: new Date(),
    updatedAt: new Date(),
};

export default function NewCampaignContainer() {
    // Wizard save (step 0): creates the campaign and returns the new ID.
    const handleWizardSave = async (campaign: Campaign): Promise<string> => {
        const result = await createAgentCampaign(campaign);
        return result.id;
    };

    // Wizard finish: persist everything before leaving the wizard. The step-0
    // create only ran once (createAgentCampaign), so re-send the base Configuration
    // fields too — otherwise step-0 edits made after going back are silently lost.
    // Contacts are persisted independently by ContactsManager. Status stays Draft —
    // the operator activates explicitly on the campaign page.
    const handleWizardComplete = async (campaign: Campaign): Promise<void> => {
        if (!campaign.id) return;
        const id = campaign.id;
        // Each write is retried independently so one transient failure (the base
        // campaign PUT can be slow / flaky under concurrent writes) doesn't reject
        // the whole batch and force the operator to redo the Finish step.
        await Promise.all([
            withRetry(() => updateAgentCampaign(id, campaign)),
            withRetry(() => updateCampaignFlow(id, {
                openingScript: campaign.openingScript,
                closingScript: campaign.closingScript,
                coreQuestions: campaign.coreQuestions,
                conditionalLogic: campaign.conditionalLogic,
                conversationMode: campaign.conversationMode,
                objective: campaign.objective,
                conversationGoal: campaign.conversationGoal,
                topicAreas: campaign.topicAreas,
                probingDepth: campaign.probingDepth,
                guardrails: campaign.guardrails,
            })),
            withRetry(() => updateCampaignEvaluation(id, campaign.evaluationCriteria)),
            withRetry(() => updateCampaignAutomation(id, {
                triggerMethod: campaign.triggerMethod,
                retryAttempts: campaign.retryAttempts,
                retryDelayMinutes: campaign.retryDelayMinutes,
                webhookUrl: campaign.webhookUrl,
                apiEndpoint: campaign.apiEndpoint,
                scheduleConfig: campaign.scheduleConfig as unknown as AutomationRules["scheduleConfig"],
            })),
        ]);
    };

    return (
        <CampaignFormContainer
            initialCampaign={emptyCampaign}
            isNew
            onWizardSave={handleWizardSave}
            onWizardComplete={handleWizardComplete}
        />
    );
}
